import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { axiosInstance } from '@/App';

/**
 * useWtfLiveData - Polls backend /api/wtf/{feed} at configurable interval.
 * Returns live WTF data with sync status, staleness detection, and graceful degradation.
 *
 * @param {string} feedName - The WTF feed to poll (e.g., 'members', 'revenue', 'footfall')
 * @param {Object} options - Configuration options
 * @param {number} options.interval - Polling interval in ms (default 300000 = 5 min)
 * @param {boolean} options.enabled - Whether polling is active (default true)
 * @param {string|null} options.gymId - Optional gym filter passed as query param
 * @returns {Object} { data, loading, error, lastSync, stale, count, refresh }
 */
export function useWtfLiveData(feedName, options = {}) {
  const { interval = 300000, enabled = true, gymId = null, leaderboard = false } = options;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [stale, setStale] = useState(false);
  const [count, setCount] = useState(0);

  const timerRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (!feedName) return;

    try {
      const params = {};
      if (gymId) params.gym_id = gymId;
      if (leaderboard) params.leaderboard = true;

      const response = await axiosInstance.get(`/wtf/${feedName}`, { params });

      if (!isMountedRef.current) return;

      const resData = response.data;
      setData(resData.data || []);
      setCount(resData.count || 0);
      setStale(resData.stale || false);
      setError(null);

      if (resData.synced_at) {
        setLastSync(new Date(resData.synced_at));
      } else {
        setLastSync(new Date());
      }

      setLoading(false);
    } catch (err) {
      if (!isMountedRef.current) return;

      // Graceful degradation: preserve stale data on fetch failure
      const message = err.response?.data?.detail || err.message || 'Failed to fetch WTF data';
      setError(message);
      setStale(true);
      setLoading(false);
      console.error(`[useWtfLiveData] Error fetching ${feedName}:`, err);
    }
  }, [feedName, gymId]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled) {
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchData();

    // Set up polling interval
    timerRef.current = setInterval(fetchData, interval);

    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [fetchData, interval, enabled]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, lastSync, stale, count, refresh };
}

/**
 * useWtfGymData - Convenience wrapper that filters useWtfLiveData results by internal_gym_id.
 *
 * @param {string} feedName - The WTF feed to poll
 * @param {string} gymId - The internal gym ID to filter by
 * @param {Object} options - Same options as useWtfLiveData
 * @returns {Object} Same as useWtfLiveData but with data filtered to matching gym
 */
export function useWtfGymData(feedName, gymId, options = {}) {
  const result = useWtfLiveData(feedName, options);

  const filteredData = useMemo(() => {
    if (!gymId || !result.data) return result.data;
    return result.data.filter(item => item.internal_gym_id === gymId);
  }, [result.data, gymId]);

  return { ...result, data: filteredData };
}

/**
 * useWtfSyncStatus - Fetches /api/wtf/sync/status for admin sync health dashboard.
 * Polls at a shorter interval since it's lightweight metadata.
 *
 * @param {number} interval - Polling interval in ms (default 300000 = 5 min)
 * @returns {Object} { feeds, loading }
 */
export function useWtfSyncStatus(interval = 300000) {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);

  const isMountedRef = useRef(true);
  const timerRef = useRef(null);

  useEffect(() => {
    // Skip polling if interval is null/0 (non-admin users)
    if (!interval) {
      setLoading(false);
      return;
    }

    isMountedRef.current = true;

    const fetchStatus = async () => {
      try {
        const res = await axiosInstance.get('/wtf/sync/status');
        if (!isMountedRef.current) return;
        setFeeds(res.data.feeds || []);
      } catch (e) {
        // Admin-only endpoint, fail silently
        if (!isMountedRef.current) return;
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchStatus();
    timerRef.current = setInterval(fetchStatus, interval);

    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [interval]);

  return { feeds, loading };
}
